import { Link } from "@tanstack/react-router";
import {
  BellRing,
  Check,
  ChevronDown,
  ChevronRight,
  Download,
  FileSpreadsheet,
  Globe,
  LogIn,
  Mail,
  MapPin,
  MessageCircle,
  MessagesSquare,
  Moon,
  PackageCheck,
  Phone,
  Play,
  ShieldCheck,
  Store,
  Sun,
  SunMoon,
  UserPlus,
  Wallet,
  Zap,
} from "lucide-react";

import { useGetPublicPlans } from "@/api/billing";
import { OfferBanner } from "@/features/offer/offer-banner";
import { Currency } from "@/components/shared/currency";
import { Button } from "@/components/ui/button";
import { getAccessToken } from "@/config";
import { useLanguage } from "@/contexts/language";
import { useTheme } from "@/contexts/theme";
import { setLanguage } from "@/i18n";
import { formatNumber } from "@/lib/format";
import { cn } from "@/lib/utils";
import { SUPPORT_WHATSAPP_DISPLAY, SUPPORT_WHATSAPP_URL } from "./contact-page";
import { CONTACT_EMAIL } from "./privacy-page";

// Where the buttons point. The store listings go live with the store releases;
// the APK is served from the website's own /downloads.
const GOOGLE_PLAY_URL =
  "https://play.google.com/store/apps/details?id=ge.iehasa.mandoob";
// Huawei AppGallery listing (the store URL from AppGallery Connect, not the
// numeric agconnect app id).
const APP_GALLERY_URL = "https://appgallery.huawei.com/app/C118908525";
// Flip to false to grey the button back out (with a "coming soon" tag)
// should the listing ever go down.
const APP_GALLERY_LIVE = true;
const APK_URL = "/downloads/mandoob.apk";

function DownloadButtons() {
  const { t } = useLanguage();
  return (
    <div className="tw:flex tw:w-full tw:max-w-md tw:flex-col tw:items-stretch tw:justify-center tw:gap-3 tw:sm:w-auto tw:sm:max-w-none tw:sm:flex-row tw:sm:flex-wrap">
      <Button asChild size="lg" className="tw:gap-2.5">
        <a href={GOOGLE_PLAY_URL} target="_blank" rel="noreferrer">
          <Play className="tw:size-5 tw:fill-current" strokeWidth={1.5} />
          {t("Mandoob:Landing.GetGooglePlay")}
        </a>
      </Button>
      {APP_GALLERY_LIVE ? (
        <Button asChild size="lg" className="tw:gap-2.5">
          <a href={APP_GALLERY_URL} target="_blank" rel="noreferrer">
            <Store className="tw:size-5" strokeWidth={2} />
            {t("Mandoob:Landing.GetAppGallery")}
          </a>
        </Button>
      ) : (
        <Button
          type="button"
          size="lg"
          variant="secondary"
          disabled
          aria-disabled="true"
          title={t("Mandoob:Landing.ComingSoon")}
          className="tw:gap-2.5 tw:text-muted-foreground"
        >
          <Store className="tw:size-5" strokeWidth={2} />
          {t("Mandoob:Landing.GetAppGallery")}
          <span className="tw:rounded-full tw:bg-background/80 tw:px-2 tw:py-0.5 tw:text-xs tw:font-medium">
            {t("Mandoob:Landing.ComingSoon")}
          </span>
        </Button>
      )}
      <Button asChild size="lg" variant="outline" className="tw:gap-2.5">
        <a href={APK_URL} download>
          <Download className="tw:size-5" strokeWidth={2} />
          {t("Mandoob:Landing.DownloadApk")}
        </a>
      </Button>
    </div>
  );
}

// Payment brands shown in the footer. Logos live in public/brands; the alt
// is the brand name, which isn't translated.
const PAYMENT_METHODS = [
  { name: "Apple Pay", src: "/brands/pay-applepay.svg" },
  { name: "Samsung Pay", src: "/brands/pay-samsungpay.svg" },
  { name: "mada", src: "/brands/pay-mada.svg" },
  { name: "Visa", src: "/brands/pay-visa.svg" },
  { name: "Mastercard", src: "/brands/pay-mastercard.svg" },
] as const;

// The three-step pitch shown right under the hero: official number, top up,
// chat from the app. Order matters — it's numbered on the page.
const HOW_IT_WORKS = [
  {
    icon: Phone,
    title: "Mandoob:Landing.HowStep1Title",
    text: "Mandoob:Landing.HowStep1Text",
  },
  {
    icon: Wallet,
    title: "Mandoob:Landing.HowStep2Title",
    text: "Mandoob:Landing.HowStep2Text",
  },
  {
    icon: MessagesSquare,
    title: "Mandoob:Landing.HowStep3Title",
    text: "Mandoob:Landing.HowStep3Text",
  },
] as const;

const FEATURES = [
  {
    icon: ShieldCheck,
    title: "Mandoob:Landing.FeatureOfficialTitle",
    text: "Mandoob:Landing.FeatureOfficialText",
  },
  {
    icon: Zap,
    title: "Mandoob:Landing.FeatureTemplatesTitle",
    text: "Mandoob:Landing.FeatureTemplatesText",
  },
  {
    icon: MessagesSquare,
    title: "Mandoob:Landing.FeatureChatTitle",
    text: "Mandoob:Landing.FeatureChatText",
  },
  {
    icon: PackageCheck,
    title: "Mandoob:Landing.FeatureStatusTitle",
    text: "Mandoob:Landing.FeatureStatusText",
  },
  {
    icon: MapPin,
    title: "Mandoob:Landing.FeatureLocationTitle",
    text: "Mandoob:Landing.FeatureLocationText",
  },
  {
    icon: FileSpreadsheet,
    title: "Mandoob:Landing.FeatureImportTitle",
    text: "Mandoob:Landing.FeatureImportText",
  },
  {
    icon: BellRing,
    title: "Mandoob:Landing.FeatureAlertsTitle",
    text: "Mandoob:Landing.FeatureAlertsText",
  },
  {
    icon: SunMoon,
    title: "Mandoob:Landing.FeatureComfortTitle",
    text: "Mandoob:Landing.FeatureComfortText",
  },
  {
    icon: UserPlus,
    title: "Mandoob:Landing.FeatureAccountTitle",
    text: "Mandoob:Landing.FeatureAccountText",
  },
] as const;

/** Public marketing page at "/": the app's features + download buttons. */
export function LandingPage() {
  const { t, dir, language } = useLanguage();
  const { resolvedTheme, setTheme } = useTheme();
  const signedIn = !!getAccessToken();
  // Public plan listing; 404s when the API hides payments (DISABLE_PAYMENTS),
  // and the whole pricing section hides with it.
  const plansQuery = useGetPublicPlans();
  const plans = plansQuery.data ?? [];
  // The largest plan (Pro) gets the "best value" spotlight.
  const bestValueId =
    plans.length > 1
      ? plans.reduce((best, p) => (p.credits > best.credits ? p : best)).id
      : null;

  return (
    <div dir={dir} className="tw:min-h-dvh tw:bg-background">
      <OfferBanner variant="landing" />
      {/* Top bar: brand + login */}
      <nav className="tw:mx-auto tw:flex tw:w-full tw:max-w-5xl tw:items-center tw:justify-between tw:gap-3 tw:px-6 tw:pt-[calc(16px+env(safe-area-inset-top))]">
        <span className="tw:flex tw:items-center tw:gap-2">
          {/* On phones the mark alone stands in for the name. */}
          <img
            src="/logo.svg"
            alt={t("Mandoob:AppName")}
            className="tw:size-11 tw:md:size-13"
          />
          <span className="tw:hidden tw:text-base tw:font-semibold tw:text-foreground tw:sm:block">
            {t("Mandoob:AppName")}
          </span>
        </span>
        <span className="tw:flex tw:items-center tw:gap-0 tw:sm:gap-0.5">
          {/* Language toggle — labeled with the language it switches TO. */}
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => setLanguage(language === "ar" ? "en" : "ar")}
            aria-label={language === "ar" ? "English" : "العربية"}
            // px-2! beats the sm variant's px-4 / has-[>svg]:px-3 — `cn` uses a
            // prefix-blind twMerge, so it can't drop the conflicting tw:px-*.
            className="tw:h-8! tw:cursor-pointer tw:gap-1 tw:px-2! tw:text-muted-foreground"
          >
            <Globe className="tw:size-4" strokeWidth={1.75} />
            {language === "ar" ? "EN" : "ع"}
          </Button>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            aria-label={t("Mandoob:Settings.Theme")}
            onClick={() =>
              setTheme(resolvedTheme === "dark" ? "light" : "dark")
            }
            className="tw:size-8 tw:cursor-pointer tw:text-muted-foreground"
          >
            {resolvedTheme === "dark" ? (
              <Sun className="tw:size-4.5" strokeWidth={1.75} />
            ) : (
              <Moon className="tw:size-4.5" strokeWidth={1.75} />
            )}
          </Button>
          <Button
            asChild
            size="sm"
            variant={signedIn ? "default" : "outline"}
            className="tw:ms-1"
          >
            <Link to={signedIn ? "/home" : "/login"}>
              <LogIn
                className="tw:size-4 tw:rtl:-scale-x-100"
                strokeWidth={2}
              />
              {signedIn
                ? t("Mandoob:Landing.OpenApp")
                : t("Mandoob:Login.SignIn")}
            </Link>
          </Button>
        </span>
      </nav>

      <div className="tw:mx-auto tw:flex tw:w-full tw:max-w-5xl tw:flex-col tw:gap-14 tw:px-6 tw:pb-16 tw:pt-10 tw:md:gap-20 tw:md:pt-16">
        {/* Hero */}
        <header className="tw:flex tw:flex-col tw:items-center tw:gap-5 tw:text-center">
          <img src="/logo.svg" alt="" className="tw:size-24 tw:md:size-28" />
          <h1 className="tw:m-0 tw:text-4xl tw:font-bold tw:text-foreground tw:md:text-5xl">
            {t("Mandoob:AppName")}
          </h1>
          <p className="tw:m-0 tw:max-w-xl tw:text-base tw:leading-relaxed tw:text-muted-foreground tw:md:text-lg">
            {t("Mandoob:Landing.Tagline")}
          </p>

          {/* Download buttons */}
          <div className="tw:mt-2 tw:flex tw:w-full tw:justify-center">
            <DownloadButtons />
          </div>
        </header>

        {/* How it works */}
        <section className="tw:flex tw:flex-col tw:gap-6">
          <div className="tw:flex tw:flex-col tw:gap-2 tw:text-center">
            <h2 className="tw:m-0 tw:text-2xl tw:font-bold tw:text-foreground">
              {t("Mandoob:Landing.HowTitle")}
            </h2>
            <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
              {t("Mandoob:Landing.HowSubtitle")}
            </p>
          </div>
          {/* Same cards as the features grid, with a "Step n" pill on the
              top-start corner (the pricing cards' badge pattern) and a small
              chevron bridging each card to the next: sideways from md up,
              downward while the cards stack. The last card has none. */}
          <ol className="tw:m-0 tw:grid tw:list-none tw:gap-5 tw:p-0 tw:pt-3 tw:md:grid-cols-3 tw:md:gap-4">
            {HOW_IT_WORKS.map(({ icon: Icon, title, text }, index) => (
              <li
                key={title}
                className="tw:relative tw:flex tw:flex-col tw:gap-2.5 tw:rounded-2xl tw:border tw:border-border tw:bg-card tw:p-5"
              >
                <span className="tw:absolute tw:-top-3 tw:start-4 tw:whitespace-nowrap tw:rounded-full tw:bg-brand tw:px-3 tw:py-1 tw:text-xs tw:font-bold tw:text-brand-foreground">
                  {t(
                    "Mandoob:Landing.HowStepLabel",
                    formatNumber(index + 1, language),
                  )}
                </span>
                <span className="tw:mt-1 tw:flex tw:size-11 tw:items-center tw:justify-center tw:rounded-xl tw:bg-brand-50 tw:text-brand">
                  <Icon className="tw:size-5.5" strokeWidth={1.75} />
                </span>
                <h3 className="tw:m-0 tw:text-base tw:font-semibold tw:text-foreground">
                  {t(title)}
                </h3>
                <p className="tw:m-0 tw:text-sm tw:leading-relaxed tw:text-muted-foreground">
                  {t(text)}
                </p>
                {index < HOW_IT_WORKS.length - 1 && (
                  <span
                    aria-hidden
                    className="tw:absolute tw:bottom-0 tw:start-1/2 tw:z-10 tw:flex tw:size-7 tw:-translate-x-1/2 tw:translate-y-1/2 tw:items-center tw:justify-center tw:rounded-full tw:border tw:border-border tw:bg-card tw:text-brand tw:shadow-sm tw:rtl:translate-x-1/2 tw:md:top-1/2 tw:md:bottom-auto tw:md:start-auto tw:md:end-0 tw:md:-translate-y-1/2 tw:md:translate-x-1/2 tw:md:rtl:-translate-x-1/2"
                  >
                    <ChevronDown
                      className="tw:size-4 tw:md:hidden"
                      strokeWidth={2.5}
                    />
                    <ChevronRight
                      className="tw:hidden tw:size-4 tw:rtl:-scale-x-100 tw:md:block"
                      strokeWidth={2.5}
                    />
                  </span>
                )}
              </li>
            ))}
          </ol>
        </section>

        {/* Features */}
        <section className="tw:flex tw:flex-col tw:gap-6">
          <h2 className="tw:m-0 tw:text-center tw:text-2xl tw:font-bold tw:text-foreground">
            {t("Mandoob:Landing.FeaturesTitle")}
          </h2>
          <div className="tw:grid tw:gap-4 tw:sm:grid-cols-2 tw:lg:grid-cols-3">
            {FEATURES.map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                className="tw:flex tw:flex-col tw:gap-2.5 tw:rounded-2xl tw:border tw:border-border tw:bg-card tw:p-5"
              >
                <span className="tw:flex tw:size-11 tw:items-center tw:justify-center tw:rounded-xl tw:bg-brand-50 tw:text-brand">
                  <Icon className="tw:size-5.5" strokeWidth={1.75} />
                </span>
                <h3 className="tw:m-0 tw:text-base tw:font-semibold tw:text-foreground">
                  {t(title)}
                </h3>
                <p className="tw:m-0 tw:text-sm tw:leading-relaxed tw:text-muted-foreground">
                  {t(text)}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Pricing — pay-as-you-go credit plans from the live catalog. */}
        {plans.length > 0 && (
          <section
            id="pricing"
            className="tw:flex tw:scroll-mt-6 tw:flex-col tw:gap-6"
          >
            <div className="tw:flex tw:flex-col tw:gap-2 tw:text-center">
              <h2 className="tw:m-0 tw:text-2xl tw:font-bold tw:text-foreground">
                {t("Mandoob:Landing.PricingTitle")}
              </h2>
              <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
                {t("Mandoob:Landing.PricingHint")}
              </p>
            </div>
            <div className="tw:grid tw:gap-4 tw:pt-3 tw:sm:grid-cols-2 tw:lg:grid-cols-3">
              {plans.map((plan, index) => (
                <div
                  key={plan.id}
                  className={cn(
                    "tw:relative tw:flex tw:flex-col tw:gap-3 tw:rounded-2xl tw:border tw:bg-card tw:p-5",
                    plan.id === bestValueId
                      ? "tw:border-brand tw:shadow-lg tw:ring-1 tw:ring-brand"
                      : "tw:border-border",
                  )}
                >
                  {/* Top plan: a green glow rising from the bottom-end
                      corner — decoration only, content sits above it. */}
                  {index === plans.length - 1 && (
                    <span
                      aria-hidden
                      className="tw:pointer-events-none tw:absolute tw:inset-0 tw:rounded-2xl tw:bg-linear-to-tl tw:from-success/25 tw:via-success/5 tw:via-35% tw:to-transparent tw:to-60% tw:rtl:bg-linear-to-tr"
                    />
                  )}
                  {plan.id === bestValueId && (
                    <span className="tw:absolute tw:-top-3 tw:start-4 tw:whitespace-nowrap tw:rounded-full tw:bg-brand tw:px-3 tw:py-1 tw:text-xs tw:font-bold tw:text-brand-foreground">
                      {t("Mandoob:Plans.BestValue")}
                    </span>
                  )}
                  <span className="tw:text-sm tw:font-semibold tw:text-foreground/80">
                    {language === "ar" ? plan.nameAr : plan.nameEn}
                  </span>
                  <span className="tw:flex tw:flex-wrap tw:items-baseline tw:gap-1.5">
                    <span className="tw:text-sm tw:text-foreground/70">
                      {t("Mandoob:Plans.UpTo")}
                    </span>
                    <span className="tw:text-3xl tw:font-bold tw:tabular-nums tw:text-foreground">
                      {formatNumber(plan.credits, language)}
                    </span>
                    <span className="tw:text-sm tw:text-foreground/70">
                      {t("Mandoob:Plans.DeliveriesUnit")}
                    </span>
                  </span>
                  <span className="tw:inline-flex tw:items-center tw:gap-1 tw:text-lg tw:font-semibold tw:tabular-nums tw:text-brand">
                    {formatNumber(plan.priceHalalas / 100, language, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                    <Currency
                      currency={plan.currency}
                      className="tw:size-4.5"
                    />
                  </span>
                  <ul className="tw:m-0 tw:mt-1 tw:flex tw:list-none tw:flex-col tw:gap-2 tw:border-t tw:border-border tw:p-0 tw:pt-3">
                    {[
                      t(
                        "Mandoob:Plans.FeatureCreditsBase",
                        formatNumber(plan.credits, language),
                      ),
                      t("Mandoob:Plans.FeatureWhatsApp"),
                      t("Mandoob:Plans.FeatureMedia"),
                      t("Mandoob:Plans.FeatureQuickActions"),
                      t("Mandoob:Plans.FeatureReplies"),
                      t(
                        "Mandoob:Plans.FeatureValidity",
                        formatNumber(plan.expiryDays, language),
                      ),
                    ].map((feature) => (
                      <li
                        key={feature}
                        className="tw:flex tw:items-start tw:gap-2 tw:text-sm tw:text-foreground/80"
                      >
                        <Check
                          className="tw:mt-0.5 tw:size-4 tw:shrink-0 tw:text-success"
                          strokeWidth={2.5}
                        />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* FAQ */}
        <section className="tw:flex tw:flex-col tw:gap-6">
          <h2 className="tw:m-0 tw:text-center tw:text-2xl tw:font-bold tw:text-foreground">
            {t("Mandoob:Landing.FaqTitle")}
          </h2>
          <div className="tw:mx-auto tw:flex tw:w-full tw:max-w-2xl tw:flex-col tw:gap-3">
            {(["1", "2", "3", "4", "5"] as const).map((n) => (
              <details
                key={n}
                className="tw:group tw:rounded-2xl tw:border tw:border-border tw:bg-card tw:p-4"
              >
                <summary className="tw:flex tw:cursor-pointer tw:list-none tw:items-center tw:justify-between tw:gap-3 tw:text-sm tw:font-semibold tw:text-foreground tw:[&::-webkit-details-marker]:hidden">
                  {t(`Mandoob:Landing.Faq${n}Q`)}
                  <ChevronDown
                    className="tw:size-4 tw:shrink-0 tw:text-muted-foreground tw:transition-transform tw:group-open:rotate-180"
                    strokeWidth={2}
                  />
                </summary>
                <p className="tw:m-0 tw:mt-3 tw:text-sm tw:leading-relaxed tw:text-muted-foreground">
                  {t(`Mandoob:Landing.Faq${n}A`)}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* Contact */}
        <section className="tw:flex tw:flex-col tw:items-center tw:gap-3 tw:text-center">
          <h2 className="tw:m-0 tw:text-2xl tw:font-bold tw:text-foreground">
            {t("Mandoob:Landing.ContactTitle")}
          </h2>
          <p className="tw:m-0 tw:max-w-md tw:text-sm tw:text-muted-foreground">
            {t("Mandoob:Landing.ContactText")}
          </p>
          <div className="tw:flex tw:w-full tw:max-w-md tw:flex-col tw:items-stretch tw:justify-center tw:gap-3 tw:sm:w-auto tw:sm:max-w-none tw:sm:flex-row">
            <Button asChild className="tw:gap-2">
              <a href={SUPPORT_WHATSAPP_URL} target="_blank" rel="noreferrer">
                <MessageCircle className="tw:size-4.5" strokeWidth={1.75} />
                {t("Mandoob:Landing.ContactWhatsAppButton")}
              </a>
            </Button>
            <Button asChild variant="outline" className="tw:gap-2">
              <a href={`mailto:${CONTACT_EMAIL}`}>
                <Mail className="tw:size-4.5" strokeWidth={1.75} />
                {t("Mandoob:Landing.ContactButton")}
              </a>
            </Button>
          </div>
          <div className="tw:flex tw:flex-col tw:items-center tw:gap-1">
            <a
              href={SUPPORT_WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              dir="ltr"
              className="tw:text-xs tw:text-muted-foreground tw:no-underline tw:hover:underline"
            >
              {SUPPORT_WHATSAPP_DISPLAY}
            </a>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              dir="ltr"
              className="tw:text-xs tw:text-muted-foreground tw:no-underline tw:hover:underline"
            >
              {CONTACT_EMAIL}
            </a>
          </div>
        </section>

        {/* Bottom call to action */}
        <section className="tw:flex tw:flex-col tw:items-center tw:gap-4 tw:rounded-3xl tw:bg-brand-50 tw:px-6 tw:py-10 tw:text-center">
          <h2 className="tw:m-0 tw:text-xl tw:font-bold tw:text-foreground tw:md:text-2xl">
            {t("Mandoob:Landing.CtaTitle")}
          </h2>
          <DownloadButtons />
        </section>

        <footer className="tw:flex tw:flex-col tw:items-center tw:gap-2 tw:text-xs tw:text-muted-foreground">
          {/* Trust row: Meta's "Tech Provider" badge and the accepted payment
              brands. Both sit on white chips so the dark-navy logos stay
              legible in dark mode. */}
          <div className="tw:mb-3 tw:flex tw:flex-col tw:items-center tw:gap-4">
            <div className="tw:flex tw:flex-col tw:items-center tw:gap-2">
              <img
                src="/brands/meta-tech-provider.svg"
                alt="Meta Tech Provider"
                className="tw:h-14 tw:rounded-xl tw:border tw:border-border tw:bg-white tw:px-4 tw:py-2"
              />
              <span className="tw:max-w-xs tw:text-center tw:font-medium tw:text-foreground/80">
                {t("Mandoob:Landing.MetaBadgeCaption")}
              </span>
            </div>
            <div className="tw:flex tw:flex-col tw:items-center tw:gap-2">
              <span>{t("Mandoob:Landing.PaymentMethods")}</span>
              <ul className="tw:m-0 tw:flex tw:list-none tw:flex-wrap tw:items-center tw:justify-center tw:gap-2 tw:p-0">
                {PAYMENT_METHODS.map(({ name, src }) => (
                  <li
                    key={name}
                    className="tw:flex tw:h-8 tw:w-13 tw:items-center tw:justify-center tw:rounded-md tw:border tw:border-border tw:bg-white tw:px-1.5"
                  >
                    <img
                      src={src}
                      alt={name}
                      title={name}
                      className="tw:max-h-5 tw:w-full tw:object-contain"
                    />
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <span className="tw:flex tw:items-center tw:gap-2">
            <img src="/logo.svg" alt="" className="tw:size-4" />
            {t("Mandoob:AppName")} · {new Date().getFullYear()}
          </span>
          <span className="tw:flex tw:items-center tw:gap-3">
            <Link
              to="/privacy"
              className="tw:text-xs tw:font-medium tw:text-muted-foreground tw:underline tw:hover:text-foreground"
            >
              {t("Mandoob:Landing.PrivacyPolicy")}
            </Link>
            <Link
              to="/contact"
              className="tw:text-xs tw:font-medium tw:text-muted-foreground tw:underline tw:hover:text-foreground"
            >
              {t("Mandoob:Landing.ContactTitle")}
            </Link>
          </span>
        </footer>
      </div>
    </div>
  );
}
